import { Fragment, useState, useContext, useEffect, useRef, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { useNodes, useReactFlow, useStore } from "reactflow";
import { styled } from "@mui/material/styles";
import List from "@mui/material/List";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import Zoom from "@mui/material/Zoom";
import { ThemedAddCircleOutlineIcon } from "assets/CXFIcons/Actions";
import Typography from "@mui/material/Typography";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { Accordion, AccordionDetails, AccordionSummary } from "../DesignerAccordion";
import { FlowContextApi, TabContextApi, FlowStateContextApi } from "../../context";
import CommonComponentMenuItem from "../CommonComponentMenuItem";
import ConnectorMenuItem from "../ConnectorMenuItem";
import { hideComponentPropsPanel } from "redux/actions/flowDesigner/componentPropsPanelActionCreator";
import { getStoredUserRecentNodes, isEmptyValue, setStoredUserRecentNodes } from "helpers/common";
import { isConnectorInFlow } from "layouts/Flow Designer/helpers/common";
import commonComponents from "layouts/Flow Designer/schemas/commonComponents";
import useComponentIdGenerator from "hooks/useComponentIdGenerator";
import useFlowStepConnection from "hooks/useFlowStepConnection";
import useLimitNumberOfNode from "hooks/useLimitNumberOfNode";
import { setCopiedNode } from "redux/actions/flowDesigner/flowsActionCreator";
import { INSERT_NODE } from "constants/flowDesigner";
import useConnectorShared from "hooks/useConnectorShared";
import { useDetermineShareFlowUserAccessType } from "hooks/useDetermineShareFlowUserAccessType";
import { usePermissions } from "hooks/usePermissions";
import useCopyPaste from "hooks/useCopyPaste";
import useAllowEditWorkflow from "hooks/useAllowEditWorkflow";
import { useCutPaste } from "hooks/useCutPaste";
import { createResourceDependency } from "redux/actions/portal/resourceDependenciesActionCreator";
import { getResourceDependencyContextId } from "helpers/resourceDependencies";

const HtmlTooltip = styled(({ className, tooltipTransformOrigin, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)(({ theme, zoomlevel, tooltipTransformOrigin }) => ({
   [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "#ffffffff",
      boxShadow: theme.shadows[1],
      color: "rgba(0, 0, 0, 0.87)",
      width: 340,
      maxWidth: 360,
      maxHeight: 600,
      height: "auto",
      borderRadius: "8px",
      overflowY: "auto",
      fontSize: theme.typography.pxToRem(12),
      padding: 0,
      marginLeft: "4px !important",
      transform: `scale(${zoomlevel <= 1 ? 1 + (1 - zoomlevel) * 2 : 1 - (zoomlevel - 1) / 2})`,
      transformOrigin: tooltipTransformOrigin
   }
}));

const StepSelectionDialogButton = ({ source, target, setActive = Function() }) => {
   const { components } = useSelector((store) => store?.designer?.cxfComponents);
   const { showComponentPropsPanel } = useSelector((store) => store?.designer?.componentPropsPanel);

   const { dragInProgress, currentSavedFlow, copiedNode, cutNode } = useSelector((store) => store?.designer?.flows);
   const [searchName, setSearchName] = useState("");
   const [popupOpen, setPopupOpen] = useState(false);
   const [eventPosition, setEventPosition] = useState({ x: 0, y: 0 });
   const { setZoomOnScroll } = useContext(FlowContextApi);
   const { autoSaveFlow } = useContext(TabContextApi);
   const { generateNodeId, generateComponentId } = useComponentIdGenerator();
   const { tryToAddStepToCanvas, tryToGenerateNodeData } = useFlowStepConnection();
   const { checkIsAllowAddNode } = useLimitNumberOfNode();
   const { isConnectorOwnedShared } = useConnectorShared();
   const dispatch = useDispatch();
   const { user } = useAuth0();
   const reactFlowInstance = useReactFlow();
   const zoomLevel = useStore((store) => store.transform[2]);
   const nodes = useNodes();
   const [tooltipTransformOrigin, setTooltipTransformOrigin] = useState("");
   const tooltipRef = useRef(null);
   const recentNodesRef = useRef([]);
   const { isReadOnlyAccess } = useDetermineShareFlowUserAccessType(currentSavedFlow?.id);
   const { canViewAnyConnectors } = usePermissions();
   const { paste } = useCopyPaste();
   const { allowEditWorkflow } = useAllowEditWorkflow();
   const isFlowActive = currentSavedFlow?.shared_status?.toLowerCase() === "active";
   const { paste: pasteCut } = useCutPaste();
   const { takeSnapshot } = useContext(FlowStateContextApi);
   const handleTooltipClose = () => {
      setPopupOpen(false);
   };

   const handleTooltipOpen = (event) => {
      if (showComponentPropsPanel) {
         dispatch(hideComponentPropsPanel());
      }

      const position = event.clientY;

      if (zoomLevel == 0.5) {
         if (position > 300) {
            setTooltipTransformOrigin("left bottom !important");
         } else if (position <= 300 && position > 176) {
            setTooltipTransformOrigin("left !important");
         } else {
            setTooltipTransformOrigin("left top !important");
         }
      } else if (zoomLevel.toFixed(2) < 1.5) {
         if (position > 485) {
            setTooltipTransformOrigin("left bottom !important");
         } else if (position <= 485 && position > 176) {
            setTooltipTransformOrigin("left !important");
         } else {
            setTooltipTransformOrigin("left top !important");
         }
      } else if (zoomLevel.toFixed(2) >= 1.6 && zoomLevel.toFixed(2) <= 1.7) {
         if (position > 485) {
            setTooltipTransformOrigin("left bottom !important");
         } else if (position <= 485 && position > 400) {
            setTooltipTransformOrigin("left 500px !important");
         } else if (position <= 400 && position > 200) {
            setTooltipTransformOrigin("left -100px !important");
         } else {
            setTooltipTransformOrigin("left top !important");
         }
      } else if (zoomLevel.toFixed(2) >= 1.5) {
         setTimeout(() => {
            const selectionTooltip = tooltipRef.current?.querySelector(".step-selection-tooltip");
            const style = selectionTooltip?.style;

            if (style?.inset === "auto auto 0px 0px") {
               setTooltipTransformOrigin("left bottom !important");
            } else if (style?.inset === "0px auto auto 0px") {
               setTooltipTransformOrigin("left top !important");
            }
         }, 50);
      }

      setPopupOpen(true);
      setEventPosition({ x: event.clientX, y: event.clientY });
   };

   const modifyInsertNodeData = (nodeData) => {
      nodeData = {
         ...copiedNode.node.data,
         flowElement: {
            ...copiedNode.node.data.flowElement
         }
      };
      delete nodeData.flowElement.nextStep;
      delete nodeData.flowElement.stepNumber;
      delete nodeData.flowElement.remarks;

      nodeData.componentKey = generateComponentId(nodeData);
      return nodeData;
   };

   const addStep = async (step, action) => {
      const checkIsAllowAddNodeResult = checkIsAllowAddNode(nodes);

      if (!checkIsAllowAddNodeResult) {
         return;
      }

      const userRecentNodes = getStoredUserRecentNodes(user?.cxfUserId);
      const nodeTypeOrId = step.type ?? step.id;
      const currentNodeCount = userRecentNodes[nodeTypeOrId];
      const updatedUserRecentNodes = {
         ...userRecentNodes,
         [nodeTypeOrId]: currentNodeCount ? currentNodeCount + 1 : 1
      };

      setStoredUserRecentNodes(user?.cxfUserId, updatedUserRecentNodes);
      recentNodesRef.current = Object.entries(updatedUserRecentNodes);

      try {
         const position = getFlowCoordinates();
         const parentNode = reactFlowInstance.getNode(source);
         const newTargetId = generateNodeId();
         let newNode = {};
         let nodeData = await tryToGenerateNodeData(step, parentNode.data, true);

         if (isEmptyValue(nodeData)) {
            handleTooltipClose();
            return;
         }

         if (action && action === INSERT_NODE) {
            nodeData = modifyInsertNodeData(nodeData);
            const targetNode = reactFlowInstance.getNode(target);

            if (targetNode) {
               nodeData.isBranch = targetNode.data.isBranch;
            }
         }

         newNode = {
            position,
            id: newTargetId,
            type: step.id ? "custom" : "common",
            data: nodeData,
            style: { opacity: 0 },
            className: "nopan"
         };

         handleTooltipClose();
         takeSnapshot();
         tryToAddStepToCanvas(source, target, newNode);

         if (copiedNode) {
            dispatch(setCopiedNode(null));
         }

         if (currentSavedFlow?.id) {
            autoSaveFlow(reactFlowInstance);
         }

         const connectorId = step.id ?? nodeData.componentId;

         if (!step.type && currentSavedFlow?.id && currentSavedFlow?.tenant_id && connectorId) {
            dispatch(
               createResourceDependency({
                  tenantId: currentSavedFlow.tenant_id,
                  sourceId: currentSavedFlow.id,
                  targetId: connectorId,
                  sourceResourceType: "flow",
                  targetResourceType: "connector",
                  contextId: getResourceDependencyContextId(currentSavedFlow.id, newTargetId)
               })
            );
         }
      } catch (e) {
         console.error(e);
      }
   };

   const getFlowCoordinates = () => {
      return reactFlowInstance.screenToFlowPosition({
         x: eventPosition.x,
         y: eventPosition.y
      });
   };

   const handleInsertCopiedNode = () => {
      takeSnapshot();
      paste(source, target);
   };

   const handleInsertCutNode = () => {
      pasteCut(source, target);
   };

   const getRecentNodeComponent = (nodeId) => commonComponents.find((component) => component.type === nodeId) || components.find((component) => component.id === nodeId);

   const syncRecentNodes = () => {
      const userRecentNodes = getStoredUserRecentNodes(user?.cxfUserId);
      recentNodesRef.current = Object.entries(userRecentNodes);

      return userRecentNodes;
   };

   const cleanupInvalidRecentNodes = () => {
      const userRecentNodes = getStoredUserRecentNodes(user?.cxfUserId);
      const validUserRecentNodes = Object.entries(userRecentNodes).reduce((validRecentNodes, [nodeId, count]) => {
         if (getRecentNodeComponent(nodeId)) {
            validRecentNodes[nodeId] = count;
         }

         return validRecentNodes;
      }, {});

      if (Object.keys(validUserRecentNodes).length !== Object.keys(userRecentNodes).length) {
         setStoredUserRecentNodes(user?.cxfUserId, validUserRecentNodes);
      }

      recentNodesRef.current = Object.entries(validUserRecentNodes);
   };

   const generateRecentNodes = () => {
      const sortedRecentNodes = [...recentNodesRef.current];
      sortedRecentNodes.sort((a, b) => b[1] - a[1]);
      const threeRecentNodes = sortedRecentNodes.slice(0, 3);

      const mappedNodes = threeRecentNodes
         .map((recentNode) => getRecentNodeComponent(recentNode[0]))
         .filter((recentNode) => !!recentNode)
         .map((recentNode) =>
            recentNode.type ? (
               <CommonComponentMenuItem
                  key={recentNode.type}
                  label={recentNode.label}
                  type={recentNode.type}
                  icon={recentNode.icon}
                  addStep={addStep}
                  allowDescription
                  allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)}
               />
            ) : (
               <ConnectorMenuItem key={recentNode.id} component={recentNode} addStep={addStep} allowDescription allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />
            )
         );

      return (
         <SoftBox
            sx={{
               marginTop: "1rem",
               textAlign: "left"
            }}
         >
            <Typography
               variant="overline"
               sx={{
                  fontWeight: "normal",
                  paddingLeft: "1rem"
               }}
            >
               Recent Nodes
            </Typography>
            <SoftBox display="flex" alignItems="center" justifyContent="start" flexWrap="wrap" gap={3} className="group-container">
               <List className="group-container-list" sx={{ width: "100%", paddingLeft: "12px" }}>
                  {mappedNodes}
               </List>
            </SoftBox>
         </SoftBox>
      );
   };

   const generateBody = () => {
      return (
         <>
            {/* Recent Nodes and Insert Buttons at the top */}
            <SoftBox sx={{ margin: "10px 10px 0 10px" }}>
               {generateRecentNodes()}
               {copiedNode && (
                  <SoftBox sx={{ margin: "10px 0" }}>
                     <SoftButton color="primary" onClick={handleInsertCopiedNode} sx={{ width: "100%" }} size="small" disabled={isFlowActive || !allowEditWorkflow(isReadOnlyAccess)}>
                        Insert copied {copiedNode?.node?.data?.label} node
                     </SoftButton>
                  </SoftBox>
               )}
               {cutNode && (
                  <SoftBox sx={{ margin: "10px 0" }}>
                     <SoftButton color="primary" onClick={handleInsertCutNode} sx={{ width: "100%" }} size="small" disabled={isFlowActive || !allowEditWorkflow(isReadOnlyAccess)}>
                        Insert cut {cutNode?.data?.label} node
                     </SoftButton>
                  </SoftBox>
               )}
            </SoftBox>
            {/* Search input */}
            <SoftBox sx={{ margin: "10px" }}>
               <SoftInput
                  className="component-search"
                  size="medium"
                  placeholder="Search..."
                  value={searchName}
                  onChange={({ currentTarget }) => {
                     setSearchName(currentTarget.value);
                  }}
               />
            </SoftBox>
            {/* Accordion lists */}
            <SoftBox className="nowheel" style={{ margin: "6px" }}>
               <SoftBox className="accordion-wrapper">
                  <Accordion key="components" className="step-selection-group group-wrapper">
                     <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
                        <Typography variant="overline">Components</Typography>
                     </AccordionSummary>
                     <AccordionDetails>
                        <SoftBox display="flex" alignItems="center" justifyContent="start" flexWrap="wrap" gap={3} className="group-container">
                           <List className="group-container-list" sx={{ width: "100%" }}>
                              {commonComponents
                                 .sortAlphabetical("label")
                                 .filter((component) => component.type !== "executionReportComponent")
                                 .map(
                                    (comp) =>
                                       (!searchName || (searchName && comp.label.toLocaleLowerCase().includes(searchName.toLocaleLowerCase()))) && (
                                          <CommonComponentMenuItem
                                             key={comp.type}
                                             label={comp.label}
                                             type={comp.type}
                                             icon={comp.icon}
                                             addStep={addStep}
                                             allowDescription
                                             allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)}
                                          />
                                       )
                                 )}
                           </List>
                        </SoftBox>
                     </AccordionDetails>
                  </Accordion>
                  <Accordion key="connectors" className="step-selection-group group-wrapper">
                     <AccordionSummary>
                        <Typography variant="overline">Connectors</Typography>
                     </AccordionSummary>
                     <AccordionDetails>
                        <SoftBox display="flex" alignItems="center" justifyContent="start" flexWrap="wrap" gap={3} className="group-container">
                           <List className="group-container-list" sx={{ width: "100%" }}>
                              {[...(components || [])]
                                 .sort((firstConnector, secondConnector) => (firstConnector.label || firstConnector.name || "").localeCompare(secondConnector.label || secondConnector.name || "", undefined, { sensitivity: "base" }))
                                 .filter(
                                    (connector) =>
                                       (isConnectorInFlow(connector, currentSavedFlow) || isConnectorOwnedShared(connector.id) || canViewAnyConnectors()) &&
                                       (!searchName || (connector.label || connector.name || "").toLocaleLowerCase().includes(searchName.toLocaleLowerCase()))
                                 )
                                 .map((comp) => (
                                    <ConnectorMenuItem key={comp.id} component={comp} addStep={addStep} allowDescription allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />
                                 ))}
                           </List>
                        </SoftBox>
                     </AccordionDetails>
                  </Accordion>
               </SoftBox>
            </SoftBox>
         </>
      );
   };

   useEffect(() => {
      if (currentSavedFlow?.steps) {
         const parsedSteps = JSON.parse(currentSavedFlow.steps);

         if (parsedSteps.copiedNode) {
            dispatch(setCopiedNode(parsedSteps.copiedNode));
         }
      }
   }, [currentSavedFlow]);

   useEffect(() => {
      syncRecentNodes();
   }, [user?.cxfUserId]);

   useEffect(() => {
      if (popupOpen) {
         cleanupInvalidRecentNodes();
      }
   }, [popupOpen, components, user?.cxfUserId]);

   useEffect(() => {
      setZoomOnScroll(!popupOpen);
      setActive(popupOpen);
   }, [popupOpen]);

   useEffect(() => {
      if (popupOpen) {
         let nodes = document.querySelectorAll(".group-wrapper.step-selection-group");
         nodes.forEach((n) => {
            n.style.display = n.querySelector(".group-container-list:empty") ? "none" : "block";
         });

         if (searchName) {
            nodes = document.querySelectorAll(".step-selection-group .MuiAccordionSummary-expandIconWrapper:not(.Mui-expanded)");
         } else {
            nodes = document.querySelectorAll(".step-selection-group .MuiAccordionSummary-expandIconWrapper.Mui-expanded");
         }
         nodes.forEach((n) => n.click());
      }
   }, [searchName]);

   return (
      <>
         <ClickAwayListener onClickAway={handleTooltipClose}>
            <div ref={tooltipRef}>
               <HtmlTooltip
                  className="step-selection-tooltip"
                  PopperProps={{
                     disablePortal: true
                  }}
                  onClose={handleTooltipClose}
                  open={popupOpen}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  placement={"right-start"}
                  arrow
                  zoomlevel={Number.isNaN(zoomLevel) ? 1 : zoomLevel}
                  title={<Fragment>{generateBody()}</Fragment>}
                  tooltipTransformOrigin={tooltipTransformOrigin}
               >
                  <button
                     disabled={currentSavedFlow?.shared_status === "active" || currentSavedFlow?.shared_status === "inactive" || !allowEditWorkflow(isReadOnlyAccess)}
                     className={`edgebutton ${!allowEditWorkflow(isReadOnlyAccess) ? "disabled" : ""}`}
                     onClick={handleTooltipOpen}
                     data-source={source}
                     data-target={target}
                     style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                     {(currentSavedFlow?.shared_status === "test" && dragInProgress) || (currentSavedFlow?.shared_status === "tests" && popupOpen) ? (
                        <Zoom in={dragInProgress || popupOpen} style={{ transitionDelay: "100ms" }}>
                           <div
                              style={{
                                 width: "22px",
                                 height: "20px",
                                 border: "1px dashed #7443FF",
                                 borderRadius: "50%"
                              }}
                           />
                        </Zoom>
                     ) : (
                        <ThemedAddCircleOutlineIcon color="#7443FF" />
                     )}
                  </button>
               </HtmlTooltip>
            </div>
         </ClickAwayListener>
      </>
   );
};

export default memo(StepSelectionDialogButton);
